/**
 * Settings Store Service
 * Manages user settings using RxDB for local persistence + replication
 * Settings sync independently from data sync with special conflict resolution:
 * - Feature flags: Server always wins (server-side feature management)
 * - Onboarding/preferences: Last write wins (handled by replication checkpoint)
 */

import { getDbForCurrentUser } from "@/services/rxdb/database";
import { SETTINGS_COLLECTION } from "@macro-tracker/models";
import type { SettingDocType, SettingTypeEnum, SettingValueMap } from "@macro-tracker/models";

/**
 * User settings entity types
 */
export type SettingType = "featureFlag" | "onboarding" | "userPreference";

/**
 * Individual setting item stored in sync storage
 */
export interface UserSetting {
  id: string;
  type: SettingType;
  key: string;
  value: boolean | string | number | object;
  updatedAt: string;
}

/**
 * Feature flag configuration (managed by server)
 */
export interface FeatureFlagSetting {
  enabled: boolean;
  updatedAt: string;
  metadata?: Record<string, string | number | boolean | null>;
}

/**
 * Onboarding status configuration
 */
export interface OnboardingSetting {
  completed: boolean;
  completedAt?: string;
  version?: number; // For future onboarding updates
}

// Map frontend SettingType to D1/RxDB doc type string
const typeToDocType = (type: SettingType): SettingTypeEnum => {
  switch (type) {
    case "featureFlag":
      return "feature-flags";
    case "onboarding":
      return "onboarding";
    case "userPreference":
      return "preferences";
  }
};

// Map D1/RxDB doc type string back to frontend SettingType
const docTypeToSettingType = (docType: string): SettingType => {
  switch (docType) {
    case "feature-flags":
      return "featureFlag";
    case "onboarding":
      return "onboarding";
    case "preferences":
      return "userPreference";
    default:
      return "userPreference";
  }
};

const getCurrentUserId = async (): Promise<string> => {
  const { useAuthSessionStore } = await import("@/stores/authSessionStore");
  return useAuthSessionStore().user?.id ?? "";
};

/** Load the settings doc for a given type (one doc per type per user). */
const getSettingDoc = async (
  userId: string,
  type: SettingType,
): Promise<SettingDocType | null> => {
  const db = await getDbForCurrentUser();
  const docId = `${userId}:${typeToDocType(type)}`;
  const doc = await db[SETTINGS_COLLECTION].findOne(docId).exec();
  return doc ? (doc.toMutableJSON() as unknown as SettingDocType) : null;
};

/**
 * Get a setting value by key and type
 */
export async function getSetting<T>(
  type: SettingType,
  key: string,
  defaultValue: T,
): Promise<T> {
  const userId = await getCurrentUserId();
  if (!userId) return defaultValue;
  const settingDoc = await getSettingDoc(userId, type);
  if (!settingDoc) return defaultValue;
  const valueMap = settingDoc.value;
  if (!(key in valueMap)) return defaultValue;
  return valueMap[key] as T;
}

/**
 * Get all settings of a specific type as a flat UserSetting array
 */
export async function getSettingsByType(type: SettingType): Promise<UserSetting[]> {
  const userId = await getCurrentUserId();
  if (!userId) return [];
  const settingDoc = await getSettingDoc(userId, type);
  if (!settingDoc) return [];
  const valueMap = settingDoc.value;
  return Object.entries(valueMap).map(([k, v]) => ({
    id: `${type}:${k}`,
    type,
    key: k,
    value: v ?? '',
    updatedAt: settingDoc.updatedAt,
  }));
}

/**
 * Save a setting value
 * Merges the key into the per-type settings doc.
 */
export async function saveSetting<T extends string | number | boolean | null | object>(
  type: SettingType,
  key: string,
  value: T,
): Promise<UserSetting> {
  const userId = await getCurrentUserId();
  const db = await getDbForCurrentUser();
  const docType = typeToDocType(type);
  const docId = `${userId}:${docType}`;
  const now = new Date().toISOString();

  const existing = await db[SETTINGS_COLLECTION].findOne(docId).exec();

  if (existing) {
    await existing.incrementalModify((d: SettingDocType) => {
      d.value = { ...d.value, [key]: value };
      d.updatedAt = now;
      return d;
    });
  } else {
    await db[SETTINGS_COLLECTION].insert({
      id: docId,
      userId,
      type: docType,
      value: { [key]: value } as SettingValueMap,
      updatedAt: now,
    });
  }

  return {
    id: `${type}:${key}`,
    type,
    key,
    value: value as boolean | string | number | object,
    updatedAt: now,
  };
}

/**
 * Delete a setting key from its type doc
 */
export async function deleteSetting(type: SettingType, key: string): Promise<boolean> {
  const userId = await getCurrentUserId();
  const db = await getDbForCurrentUser();
  const docId = `${userId}:${typeToDocType(type)}`;
  const existing = await db[SETTINGS_COLLECTION].findOne(docId).exec();
  if (!existing) return false;
  await existing.incrementalModify((d: SettingDocType) => {
    const { [key]: _removed, ...rest } = d.value;
    d.value = rest;
    d.updatedAt = new Date().toISOString();
    return d;
  });
  return true;
}

/**
 * Clear all settings (removes all setting docs for the current user)
 */
export async function clearAllSettings(): Promise<void> {
  const db = await getDbForCurrentUser();
  const docs = await db[SETTINGS_COLLECTION].find().exec();
  await Promise.all(docs.map((d) => d.remove()));
}

// ---------------------------------------------------------------------------
// Legacy sync stubs — no-ops now that RxDB handles sync.
// ---------------------------------------------------------------------------

export async function getPendingSettingsSync(): Promise<UserSetting[]> {
  return [];
}

export async function applySettingsFromSync(
  _serverSettings: Array<{
    id: string;
    type: SettingType;
    key: string;
    value: unknown;
    updatedAt: string;
  }>,
): Promise<void> {
  // No-op: RxDB replication handles server → client sync.
}

export async function markAllSettingsForSync(): Promise<void> {
  // No-op: RxDB replication handles client → server sync.
}

// ---------------------------------------------------------------------------
// Compatibility shim: legacy callers that use settingsStore.storage.getById
// or settingsStore.storage.removeFromSync with the old "${type}:${key}" ID format.
// ---------------------------------------------------------------------------
const legacyStorageShim = {
  /**
   * No-op: RxDB replication handles sync tracking; no manual sync records exist.
   */
  removeFromSync: async (_settingId: string): Promise<void> => {
    // No-op in the RxDB world — sync is handled by replication checkpoints.
  },

  getById: async (settingId: string): Promise<UserSetting | null> => {
    const colonIdx = settingId.indexOf(":");
    if (colonIdx === -1) return null;
    const rawType = settingId.slice(0, colonIdx) as SettingType;
    const key = settingId.slice(colonIdx + 1);

    // rawType might be a doc-type string ("feature-flags") or a SettingType ("featureFlag")
    const type: SettingType =
      rawType === "featureFlag" || rawType === "onboarding" || rawType === "userPreference"
        ? rawType
        : docTypeToSettingType(rawType);

    const userId = await getCurrentUserId();
    if (!userId) return null;
    const settingDoc = await getSettingDoc(userId, type);
    if (!settingDoc) return null;
    const valueMap = settingDoc.value;
    if (!(key in valueMap)) return null;
    return {
      id: settingId,
      type,
      key,
      value: valueMap[key] ?? '',
      updatedAt: settingDoc.updatedAt,
    };
  },
};

/**
 * Settings Store API
 */
export const settingsStore = {
  get: getSetting,
  getByType: getSettingsByType,
  save: saveSetting,
  delete: deleteSetting,
  clear: clearAllSettings,
  getPendingSync: getPendingSettingsSync,
  applyFromSync: applySettingsFromSync,
  markAllForSync: markAllSettingsForSync,
  // Expose legacy-compatible storage shim for advanced use cases
  storage: legacyStorageShim,
};

export default settingsStore;
